import { ExclamationCircleFilled } from '@ant-design/icons';
import { ConnectError } from '@bufbuild/connect';
import { Form, Modal, notification, Typography } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { EffectProductStateRemoveReasonCode } from 'protocol/api/billing_new/dto_order_new_pb';

import { useAppDispatch } from '@/shared/hooks';

import { i18n } from '../../config';
import * as billingModel from '../../model';
import { OrderReasonFields } from '../orderReasonFields';

import styles from './RemoveProductModal.module.scss';

const { Text } = Typography;

type RemoveProductReason = Exclude<
  EffectProductStateRemoveReasonCode,
  | EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_InvalidValue
  | EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_AutoReplacedOnLimitExhaustion
>;

type RemoveProductFormFields = {
  reason: RemoveProductReason;
  comment?: string;
};

type RemoveProductModalProps = {
  visible: boolean;
  productID: string;
  productType: 'subscription' | 'package';
  accountID: string;
  canEditProducts: boolean;
  onCancel: () => void;
};

const reasonOptions: RemoveProductReason[] = [
  EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_LimitExhaustedWithReplacement,
  EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_DiagnocatEmployeeError,
  EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_ReactivationByAgreement,
  EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_ContractTermination,
  EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_ConfigurationTesting,
];

export const RemoveProductModal = (props: RemoveProductModalProps) => {
  const {
    visible,
    productID,
    productType,
    accountID,
    canEditProducts,
    onCancel,
  } = props;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm<RemoveProductFormFields>();

  const [loading, setLoading] = useState(false);

  const reason = Form.useWatch('reason', form);
  const messages =
    productType === 'package'
      ? i18n.removePackageModalMessages
      : i18n.removeSubscriptionModalMessages;

  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  const handleCancel = () => {
    if (loading) {
      return;
    }

    handleClose();
  };

  const handleSubmit = async () => {
    if (loading) {
      return;
    }

    if (!canEditProducts) {
      notification.error({
        message: formatMessage(messages.noPermission),
      });

      return;
    }

    setLoading(true);

    try {
      const values = await form.validateFields();

      await dispatch(
        billingModel.thunks.createOrder({
          AccountID: accountID,
          Effect: {
            case: 'ProductStateRemove',
            value: {
              RemoveProductStateID: productID,
              Reason: values.reason,
              Comment: values.comment,
            },
          },
        }),
      ).unwrap();

      notification.success({
        message: formatMessage(messages.success),
      });

      handleClose();
    } catch (error: unknown) {
      const { message } = (error ?? {}) as ConnectError;

      notification.error({
        message:
          message || formatMessage(i18n.removeSubscriptionModalMessages.error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span className={styles.title}>
          <ExclamationCircleFilled
            className={styles.titleIcon}
            rev={undefined}
          />
          {formatMessage(messages.title)}
        </span>
      }
      open={visible}
      closable={!loading}
      maskClosable={!loading}
      keyboard={!loading}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText={formatMessage(messages.okText)}
      cancelText={formatMessage(messages.cancelText)}
      cancelButtonProps={{ disabled: loading }}
      okButtonProps={{
        danger: true,
        loading,
        disabled: !reason || !canEditProducts,
      }}
      centered
      width={600}
    >
      <div className={styles.warning}>
        <Text>{formatMessage(messages.content)}</Text>
        <Text strong>{formatMessage(messages.confirmation)}</Text>
      </div>

      <Form form={form} layout="vertical" disabled={loading}>
        <OrderReasonFields
          reasonOptions={reasonOptions}
          reasonMessages={i18n.removeProductReasonMessages}
        />
      </Form>
    </Modal>
  );
};
